import React, { useState, useEffect } from 'react';
import { X, Loader, ExternalLink } from 'lucide-react';
import { apiService } from '../services/api';
import NotionFieldMapping from './NotionFieldMapping';
import './NotionImport.css';

interface NotionImportProps {
  onComplete: (skipped?: boolean) => void;
  initialStep?: Step;
}

type Step = 'connect' | 'connecting' | 'select-page' | 'mapping' | 'syncing' | 'complete';

interface FieldMapping {
  [notionColumnName: string]: {
    mantisField: string;
    type: 'standard' | 'custom';
    customFieldId?: string;
    notionType: string;
  };
}

// Helper function to dynamically determine API URL based on environment
const getApiUrl = (): string => {
  const configuredUrl = process.env.REACT_APP_API_URL;

  // If we're on localhost, always use backend on port 3001
  if (window.location.origin.includes('localhost')) {
    // If configured URL is provided and points to port 3001 (backend), use it
    if (configuredUrl && configuredUrl.includes('localhost:3001')) {
      // Ensure it has /api
      if (configuredUrl.endsWith('/api')) {
        return configuredUrl;
      }
      return configuredUrl.endsWith('/') ? `${configuredUrl}api` : `${configuredUrl}/api`;
    }
    // Always default to backend on port 3001 for localhost
    return 'http://localhost:3001/api';
  }

  // For deployed environments, derive API URL from current origin
  return `${window.location.origin}/api`;
};

const NotionImport: React.FC<NotionImportProps> = ({ onComplete, initialStep }) => {
  const [step, setStep] = useState<Step>(initialStep || 'connect');
  const [pageUrl, setPageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [mapping, setMapping] = useState<FieldMapping | null>(null);
  const [notionColumns, setNotionColumns] = useState<string[]>([]);
  const [entryCount, setEntryCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string>('');
  const [initialSyncDone, setInitialSyncDone] = useState(false);
  const popupRef = React.useRef<Window | null>(null);
  const popupCheckInterval = React.useRef<NodeJS.Timeout | null>(null);

  // Check connection status on mount (unless we're starting at a specific step)
  useEffect(() => {
    if (initialStep === 'mapping') {
      // If starting at mapping step, load the mapping
      loadMappingForEdit();
    } else {
      checkConnectionStatus();
    }
  }, []);

  const loadMappingForEdit = async () => {
    try {
      const mappingData = await apiService.getNotionFieldMapping();
      const status = await apiService.getNotionStatus();
      
      if (mappingData && mappingData.mapping && status.pageId) {
        // Fetch sample entry count from status or make a call to get it
        setMapping(mappingData.mapping);
        setNotionColumns(Object.keys(mappingData.mapping));
        setEntryCount(status.importedFoundersCount || 0);
        setStep('mapping');
        setIsConnected(true);
      }
    } catch (err) {
      console.error('Error loading mapping:', err);
      setError('Failed to load mapping. Please reconnect your Notion page.');
    }
  };

  // Auto-advance to select-page if connected but still on connect step
  useEffect(() => {
    if (isConnected && (step === 'connect' || step === 'connecting')) {
      checkConnectionStatus();
    }
  }, [isConnected, step]);

  // Auto-trigger initial sync when reaching complete step
  useEffect(() => {
    if (step === 'complete' && !initialSyncDone && !syncing) {
      setInitialSyncDone(true);
      // Trigger sync
      setSyncing(true);
      setError('');
      setSyncResult('');

      apiService.triggerNotionSync()
        .then((result) => {
          setSyncResult(
            `✅ Sync completed! ` +
            `Created: ${result.result.created}, ` +
            `Updated: ${result.result.updated}, ` +
            `Errors: ${result.result.errors}`
          );
        })
        .catch((err: any) => {
          const errorMessage = err.response?.data?.error || err.message || 'Failed to sync from Notion';
          setError(errorMessage);
        })
        .finally(() => {
          setSyncing(false);
        });
    }
  }, [step, initialSyncDone, syncing]);

  // Cleanup popup check interval on unmount
  useEffect(() => {
    return () => {
      if (popupCheckInterval.current) {
        clearInterval(popupCheckInterval.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Listen for OAuth callback
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security - accept messages from same origin
      const expectedOrigin = window.location.origin;
      if (event.origin !== expectedOrigin && !event.origin.includes('localhost')) {
        return;
      }

      if (event.data.type === 'NOTION_OAUTH_SUCCESS') {
        setStep('select-page');
        setIsConnected(true);
        setError('');
        setLoading(false); // Clear loading state
        if (popupRef.current) {
          popupRef.current.close();
        }
        if (popupCheckInterval.current) {
          clearInterval(popupCheckInterval.current);
          popupCheckInterval.current = null;
        }
      } else if (event.data.type === 'NOTION_OAUTH_ERROR') {
        setError(event.data.error || 'Failed to connect Notion');
        setStep('connect');
        setLoading(false);
        if (popupRef.current) {
          popupRef.current.close();
        }
        if (popupCheckInterval.current) {
          clearInterval(popupCheckInterval.current);
          popupCheckInterval.current = null;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const status = await apiService.getNotionStatus();
      if (status.connected) {
        setIsConnected(true);
        if (status.pageId) {
          // Already have page connected, go to complete
          setStep('complete');
        } else {
          setStep('select-page');
        }
      }
    } catch (err) {
      console.error('Error checking Notion status:', err);
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError('');

      // Get API base URL dynamically based on environment
      const apiUrl = getApiUrl();
      // Ensure we have the correct API URL format - remove trailing slashes and ensure /api is present
      let cleanApiUrl = apiUrl.trim().replace(/\/+$/, ''); // Remove trailing slashes
      if (!cleanApiUrl.endsWith('/api')) {
        cleanApiUrl = cleanApiUrl.endsWith('/') ? `${cleanApiUrl}api` : `${cleanApiUrl}/api`;
      }
      let authUrl = `${cleanApiUrl}/notion/auth/connect`;

      // Validate that the URL is absolute
      if (!authUrl.startsWith('http://') && !authUrl.startsWith('https://')) {
        throw new Error(`Invalid OAuth URL: ${authUrl}. URL must be absolute.`);
      }
      
      // Ensure we're not accidentally using the frontend port (3000) for the backend endpoint
      if (authUrl.includes('localhost:3000') && authUrl.includes('/api/')) {
        console.warn('⚠️ OAuth URL is using frontend port (3000) instead of backend port (3001)');
        // Replace frontend port with backend port
        authUrl = authUrl.replace('localhost:3000', 'localhost:3001');
        console.log('🔧 Corrected URL:', authUrl);
      }

      console.log('🔗 Notion OAuth URL construction:', {
        originalApiUrl: apiUrl,
        cleanApiUrl,
        finalAuthUrl: authUrl,
        windowOrigin: window.location.origin,
        envVar: process.env.REACT_APP_API_URL
      });

      // Open OAuth in popup window
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      const popup = window.open(
        authUrl,
        'notion-oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        setError('Popup blocked. Please allow popups for this site and try again.');
        setLoading(false);
        return;
      }

      popupRef.current = popup;
      setStep('connecting');

      // Poll for popup closure
      popupCheckInterval.current = setInterval(() => {
        if (popup.closed) {
          if (popupCheckInterval.current) {
            clearInterval(popupCheckInterval.current);
            popupCheckInterval.current = null;
          }
          if (step === 'connecting') {
            // User closed popup without completing
            setError('Connection cancelled. Please try again.');
            setStep('connect');
            setLoading(false);
          }
        }
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to open connection window');
      setLoading(false);
    }
  };

  const handleConnectPage = async () => {
    if (!pageUrl.trim()) {
      setError('Please enter a Notion page URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔗 Connecting Notion page:', pageUrl);
      const response = await apiService.connectNotionPage(pageUrl);
      console.log('✅ Notion page connected:', response);

      // No mapping UI needed - go straight to complete
      setInitialSyncDone(false); // Reset to trigger auto-sync on complete step
      setStep('complete');
      setLoading(false);
    } catch (err: any) {
      console.error('❌ Error connecting Notion page:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to connect page';
      
      // If it's a sharing error, provide more detailed instructions
      if (errorMessage.includes('not accessible') || errorMessage.includes('shared') || errorMessage.includes('unauthorized')) {
        setError(
          errorMessage + '\n\n' +
          '📋 To share your page:\n' +
          '1. In Notion, open your page and click "Share" (top right)\n' +
          '2. In the search field, type your integration name (check Settings → Connections if unsure)\n' +
          '3. Select the integration and grant it Read or Edit access\n' +
          '4. Then try connecting the page again here'
        );
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (popupCheckInterval.current) {
      clearInterval(popupCheckInterval.current);
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    onComplete(true);
  };

  const handleComplete = () => {
    onComplete(false);
  };

  const handleMappingConfirm = async (confirmedMapping: FieldMapping) => {
    setLoading(true);
    setError('');

    try {
      await apiService.confirmNotionMapping(confirmedMapping);

      setInitialSyncDone(false); // Reset to trigger auto-sync on complete step
      setStep('complete');
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to complete import');
      setLoading(false);
    }
  };

  const handleMappingCancel = () => {
    setMapping(null);
    setNotionColumns([]);
    setEntryCount(0);
    // If opened directly for editing (initialStep === 'mapping'), close the modal
    // Otherwise, go back to select-page (part of normal flow)
    if (initialStep === 'mapping') {
      onComplete(true);
    } else {
      setStep('select-page');
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setError('');
    setSyncResult('');

    try {
      const result = await apiService.triggerNotionSync();
      setSyncResult(
        `✅ Sync completed! ` +
        `Created: ${result.result.created}, ` +
        `Updated: ${result.result.updated}, ` +
        `Errors: ${result.result.errors}`
      );
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to sync from Notion';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  // Step 1: Connect Notion
  // Skip this step if already connected and no page is set (should show select-page instead)
  if ((step === 'connect' || step === 'connecting') && !isConnected) {
    return (
      <div className="notion-import-container">
        <button
          className="notion-import-close-button"
          onClick={handleSkip}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="notion-import-content">
          <div className="notion-import-header">
            <h1>Connect Notion</h1>
            <p>Link your Notion workspace to import data</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="notion-import-instructions">
            <ol>
              <li>Click "Connect Notion" below</li>
              <li>Authorize Remi AI in the popup window</li>
              <li>Select the Notion page you want to sync</li>
            </ol>
          </div>

          <button
            className="notion-connect-button"
            onClick={handleConnect}
            disabled={loading || step === 'connecting'}
          >
            {step === 'connecting' ? (
              <>
                <Loader className="spinner" size={20} />
                Connecting...
              </>
            ) : (
              <>
                <ExternalLink size={20} />
                Connect Notion
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Select Page
  if (step === 'select-page') {
    return (
      <div className="notion-import-container">
        <button
          className="notion-import-close-button"
          onClick={handleSkip}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="notion-import-content">
          <div className="notion-import-header">
            <h1>Connect Your Notion Page</h1>
            <p>Paste your Notion page URL below</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="notion-import-instructions">
            <ol>
              <li>Click on the three dots (⋯) on the top right of your Notion page</li>
              <li>Copy the link and paste it below</li>
            </ol>
          </div>

          <div className="notion-page-input">
            <input
              type="text"
              placeholder="https://notion.so/workspace/page-id"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !loading && pageUrl.trim()) {
                  handleConnectPage();
                }
              }}
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                handleConnectPage();
              }}
              disabled={loading || !pageUrl.trim()}
              style={{ opacity: loading || !pageUrl.trim() ? 0.6 : 1 }}
            >
              {loading ? 'Connecting...' : 'Connect Page'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Field Mapping
  if (step === 'mapping' && mapping && notionColumns.length > 0) {
    return (
      <div className="notion-import-container">
        <button
          className="notion-import-close-button"
          onClick={handleMappingCancel}
          title="Cancel"
        >
          <X size={24} />
        </button>
        <div className="notion-import-content has-mapping">
          {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
          <NotionFieldMapping
            mapping={mapping}
            notionColumns={notionColumns}
            entryCount={entryCount}
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
          />
        </div>
      </div>
    );
  }

  // Step 4: Syncing
  if (step === 'syncing') {
    return (
      <div className="notion-import-container">
        <div className="notion-import-content">
          <div className="notion-import-header">
            <Loader className="spinner" size={48} />
            <h2>Syncing from Notion...</h2>
            <p>Importing your data</p>
          </div>
        </div>
      </div>
    );
  }

  // Step 5: Complete
  if (step === 'complete') {
    return (
      <div className="notion-import-container">
        <button
          className="notion-import-close-button"
          onClick={handleComplete}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="notion-import-content">
          <div className="notion-import-header">
            {syncing ? (
              <>
                <Loader className="spinner" size={48} />
                <h2>Syncing from Notion...</h2>
                <p>Importing your data</p>
              </>
            ) : (
              <>
                <h2>✅ Connected!</h2>
                <p>Your Notion page is now connected to Remi AI</p>
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                  Changes sync automatically every 5 minutes
                </p>
              </>
            )}
          </div>

          {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

          {!syncing && (
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button 
                className="notion-connect-button" 
                onClick={handleComplete}
                style={{ backgroundColor: '#28a745' }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default NotionImport;

